import { useEffect, useRef, useState } from "react";
import { IMAGES } from "@/lib/constants";

interface AwardSealProps {
  variant: "light" | "dark";
  placement: "above" | "below";
  tooltipId: string;
  imageClassName: string;
  width: number;
  height: number;
  priority?: boolean;
}

export default function AwardSeal({
  variant,
  placement,
  tooltipId,
  imageClassName,
  width,
  height,
  priority = false,
}: AwardSealProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!buttonRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.blur();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const tooltipPosition = placement === "below"
    ? "top-full mt-3 after:bottom-full after:border-b-white"
    : "bottom-full mb-3 after:top-full after:border-t-white";
  const focusRing = variant === "light"
    ? "focus-visible:ring-[#2563EB] focus-visible:ring-offset-white"
    : "focus-visible:ring-[#60A5FA] focus-visible:ring-offset-gray-900";

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => setIsOpen(open => !open)}
      onBlur={() => setIsOpen(false)}
      aria-label="Information om utmärkelsen Branschvinnare 2026"
      aria-controls={tooltipId}
      aria-describedby={tooltipId}
      aria-expanded={isOpen}
      className={`group relative inline-flex cursor-help appearance-none rounded-sm border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-4 ${focusRing}`}
    >
      <img
        src={variant === "light" ? IMAGES.branschvinnare2026Light : IMAGES.branschvinnare2026}
        alt="ASPAB – Branschvinnare 2026"
        className={imageClassName}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
      />
      <span
        id={tooltipId}
        role="tooltip"
        className={`pointer-events-none absolute left-1/2 z-[60] w-64 -translate-x-1/2 rounded-md bg-white px-4 py-3 text-left text-xs leading-relaxed text-gray-800 shadow-xl transition-opacity duration-200 after:absolute after:left-1/2 after:-translate-x-1/2 after:border-[6px] after:border-transparent after:content-[''] group-hover:opacity-100 group-focus-visible:opacity-100 ${tooltipPosition} ${isOpen ? "opacity-100" : "opacity-0"}`}
      >
        <strong className="mb-1 block text-sm text-gray-950">
          Branschvinnare 2026
        </strong>
        Utmärkelsen ges till företag som överträffar sin bransch i omsättning
        och resultat och samtidigt visar vinst.
      </span>
    </button>
  );
}
